import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdArrowForward,
  MdApps,
  MdAssignment,
  MdEventNote,
  MdSchool,
  MdPerson,
  MdPersonAdd,
} from "react-icons/md";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import { isConfiguredCampusClass } from "../../../utils/campusClasses";
import { getStoredCampusSections } from "../../../utils/campusSections";
import StaffManagement from "./StaffManagement";
import "./CampusDetailView.css";

/**
 * CampusDetailView - Displays campus management actions for a selected campus.
 */
function CampusDetailView({ campusId, campusName, onLogout, showToast }) {
  const navigate = useNavigate();
  const isSuperadmin =
    (localStorage.getItem("als_role") || "").toLowerCase() === "superadmin";
  const [stats, setStats] = useState({
    sessions: 0,
    classes: 0,
    sections: 0,
    assignedClasses: 0,
    sectionedClasses: 0,
    setupCompleteClasses: 0,
    staffCount: 0,
  });
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    document.body.classList.add("campus-detail-page-active");
    return () => {
      document.body.classList.remove("campus-detail-page-active");
    };
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [sessionsRes, classesRes, staffRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/sessions?campus_id=${campusId}`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/api/classes?campus_id=${campusId}`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/api/campuses/${campusId}/staff`, {
            headers: getAuthHeaders(),
          }),
        ]);

        if (sessionsRes.status === 401 || classesRes.status === 401 || staffRes.status === 401) {
          onLogout?.();
          return;
        }
        if (!sessionsRes.ok || !classesRes.ok) {
          throw new Error("Could not load campus counts");
        }

        const [sessionsData, classesData, staffData] = await Promise.all([
          sessionsRes.json(),
          classesRes.json(),
          staffRes.ok ? staffRes.json() : [],
        ]);
        const campusClasses = Array.isArray(classesData)
          ? classesData.filter(isConfiguredCampusClass)
          : [];
        const classSectionsOf = (classItem) => {
          const values = Array.isArray(classItem?.sections) && classItem.sections.length
            ? classItem.sections
            : classItem?.section
              ? [classItem.section]
              : [];
          return [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))];
        };
        const classSessionIdsOf = (classItem) => {
          const values = Array.isArray(classItem?.session_ids) && classItem.session_ids.length
            ? classItem.session_ids
            : classItem?.session_id
              ? [classItem.session_id]
              : [];
          return [...new Set(values.map((value) => String(value || "")).filter(Boolean))];
        };

        const storedSections = getStoredCampusSections(campusId);

        setStats((prev) => ({
          ...prev,
          sessions: Array.isArray(sessionsData) ? sessionsData.length : 0,
          classes: campusClasses.length,
          sections: storedSections.length,
          assignedClasses: campusClasses.filter(
            (classItem) => classSessionIdsOf(classItem).length > 0,
          ).length,
          sectionedClasses: campusClasses.filter((classItem) =>
            classSectionsOf(classItem).length > 0,
          ).length,
          setupCompleteClasses: campusClasses.filter(
            (classItem) =>
              classSessionIdsOf(classItem).length > 0 &&
              classSectionsOf(classItem).length > 0,
          ).length,
          staffCount: Array.isArray(staffData) ? staffData.length : 0,
        }));
      } catch (error) {
        console.error("Failed to load campus stats", error);
        showToast?.("error", "Load Error", "Could not load campus counts.");
      }
    };

    if (campusId) loadStats();
  }, [campusId, onLogout, showToast]);

  useEffect(() => {
    const refreshSections = () => {
      setStats((prev) => ({
        ...prev,
        sections: getStoredCampusSections(campusId).length,
      }));
    };

    window.addEventListener("campus-sections-updated", refreshSections);
    window.addEventListener("storage", refreshSections);
    return () => {
      window.removeEventListener("campus-sections-updated", refreshSections);
      window.removeEventListener("storage", refreshSections);
    };
  }, [campusId]);

  const handleSessionsClick = () => {
    navigate(`/superadmin/campuses/${campusId}/sessions`);
  };

  const handleClassesClick = () => {
    navigate(`/superadmin/campuses/${campusId}/classes`);
  };

  const handleSectionsClick = () => {
    navigate(`/superadmin/campuses/${campusId}/sections`);
  };

  const handleAssignmentsClick = () => {
    navigate(`/superadmin/campuses/${campusId}/section-assignments`);
  };

  const handleStaffClick = () => {
    setActiveTab("staff");
  };

  const setupCompleteClasses = stats.setupCompleteClasses;
  const incompleteSetupClasses = Math.max(
    stats.classes - setupCompleteClasses,
    0,
  );
  const setupRate = stats.classes
    ? Math.round((setupCompleteClasses / stats.classes) * 100)
    : 0;
  const sessionRate = stats.classes
    ? Math.round((stats.assignedClasses / stats.classes) * 100)
    : 0;
  const sectionAssignmentRate = stats.classes
    ? Math.round((stats.sectionedClasses / stats.classes) * 100)
    : 0;
  const isReady =
    stats.sessions > 0 &&
    stats.classes > 0 &&
    stats.sections > 0 &&
    incompleteSetupClasses === 0;
  const nextAction =
    stats.sessions === 0
      ? {
          label: "Create First Session",
          helper: "Start with the academic year or term.",
          onClick: handleSessionsClick,
        }
      : stats.classes === 0
        ? {
            label: "Add Classes",
            helper: "Add the class names for this campus.",
            onClick: handleClassesClick,
          }
        : stats.sections === 0
          ? {
              label: "Create Sections",
              helper:
                "Create the section names before assigning them to classes.",
              onClick: handleSectionsClick,
            }
          : !isReady
            ? {
                label: "Complete Class Setup",
                helper:
                  "Assign or update a section and session for each class.",
                onClick: handleAssignmentsClick,
              }
            : {
                label: "Review Class Setup",
                helper:
                  "All classes have sections and sessions. Review or adjust assignments.",
                onClick: handleAssignmentsClick,
              };

  const cards = [
    {
      key: "sessions",
      step: "Step 1",
      title: "Sessions",
      description:
        "Create the academic year or term before organizing classes.",
      metric: stats.sessions,
      metricLabel: "Total sessions",
      icon: <MdEventNote />,
      action: "View Sessions",
      className: "sessions-card",
      onClick: handleSessionsClick,
    },
    {
      key: "classes",
      step: "Step 2",
      title: "Classes",
      description: "Add the class names that belong to this campus.",
      metric: stats.classes,
      metricLabel: "Total classes",
      icon: <MdSchool />,
      action: "View Classes",
      className: "classes-card",
      onClick: handleClassesClick,
    },
    {
      key: "setup",
      step: "Step 4",
      title: "Class Setup",
      description:
        "Assign or update one section and one session for each class.",
      metric: setupCompleteClasses,
      metricLabel: "Classes setup",
      icon: <MdAssignment />,
      action: "Open Setup",
      className: "assignments-card",
      onClick: handleAssignmentsClick,
    },
  ];

  cards.splice(2, 0, {
    key: "sections",
    step: "Step 3",
    title: "Sections",
    description: "Create the section names that classes can use during setup.",
    metric: stats.sections,
    metricLabel: "Total sections",
    icon: <MdApps />,
    action: "View Sections",
    className: "sections-card",
    onClick: handleSectionsClick,
  });

  // Add Staff card
  cards.push({
    key: "staff",
    step: "Staff",
    title: "Staff Management",
    description: "Manage teachers, administrators, and support staff for this campus.",
    metric: stats.staffCount,
    metricLabel: "Total staff",
    icon: <MdPerson />,
    action: "Manage Staff",
    className: "staff-card",
    onClick: handleStaffClick,
  });

  // If staff tab is active, show StaffManagement component
  if (activeTab === "staff") {
    return (
      <div className="campus-detail-container">
        <div className="campus-detail-header-with-back">
          <button 
            className="back-to-overview-btn"
            onClick={() => setActiveTab("overview")}
          >
            ← Back to Overview
          </button>
          <h2 className="detail-title">{campusName}</h2>
        </div>
        <StaffManagement
          campusId={campusId}
          campusName={campusName}
          showToast={showToast}
          onLogout={onLogout}
          onBack={() => setActiveTab("overview")}
        />
      </div>
    );
  }

  return (
    <div className="campus-detail-container">
      <section className="campus-detail-hero">
        <div className="campus-hero-main">
          <div>
            <span className="campus-eyebrow">Campus Management</span>
            <h2 className="detail-title">{campusName}</h2>
          </div>
        </div>

        <div className="campus-hero-stats">
          <div className="hero-stat">
            <span>{stats.sessions}</span>
            <strong>Sessions</strong>
          </div>
          <div className="hero-stat">
            <span>{stats.classes}</span>
            <strong>Classes</strong>
          </div>
          <div className="hero-stat">
            <span>{stats.sections}</span>
            <strong>Sections</strong>
          </div>
          <div className="hero-stat">
            <span>{stats.staffCount}</span>
            <strong>Staff</strong>
          </div>
        </div>
      </section>

      <div className="campus-progress-strip">
        <div>
          <span className="campus-eyebrow">Setup progress</span>
          <strong>
            {setupCompleteClasses} of {stats.classes} classes are setup
          </strong>
        </div>
        <div
          className="campus-progress-track"
          aria-label={`${setupRate}% classes are setup`}
        >
          <span style={{ width: `${setupRate}%` }} />
        </div>
        <small>{incompleteSetupClasses} need setup</small>
      </div>

      {isSuperadmin && (
      <section className="campus-next-step">
        <div>
          <span className="campus-eyebrow">Recommended next step</span>
          <strong>{nextAction.label}</strong>
          <p>{nextAction.helper}</p>
        </div>
        <button onClick={nextAction.onClick}>
          Continue <MdArrowForward />
        </button>
      </section>
      )}

      <div className="campus-setup-checklist">
        <div className={stats.sessions > 0 ? "done" : ""}>
          <span>1</span>
          <strong>Session</strong>
          <small>
            {stats.sessions > 0 ? `${stats.sessions} created` : "Not started"}
          </small>
        </div>
        <div className={stats.classes > 0 ? "done" : ""}>
          <span>2</span>
          <strong>Classes</strong>
          <small>
            {stats.classes > 0 ? `${stats.classes} created` : "Not started"}
          </small>
        </div>
        <div className={stats.sections > 0 ? "done" : ""}>
          <span>3</span>
          <strong>Sections</strong>
          <small>
            {stats.sections > 0 ? `${stats.sections} created` : "Not started"}
          </small>
        </div>
        <div className={isReady ? "done" : ""}>
          <span>4</span>
          <strong>Class setup</strong>
          <small>
            {Math.min(sectionAssignmentRate, sessionRate)}% complete
          </small>
        </div>
        <div className={stats.staffCount > 0 ? "done" : ""}>
          <span>5</span>
          <strong>Staff</strong>
          <small>
            {stats.staffCount > 0 ? `${stats.staffCount} members` : "Not started"}
          </small>
        </div>
      </div>

      <div className="campus-cards-container">
        {cards.map((card) => (
          <article
            key={card.key}
            className={`campus-option-card ${card.className}`}
            onClick={card.onClick}
          >
            <div className="card-topline">
              <div className="card-icon-wrapper">
                <span className="card-icon">{card.icon}</span>
              </div>
              <div className="card-stats">
                <span className="stat-count">{card.metric}</span>
                <span className="stat-label">{card.metricLabel}</span>
              </div>
            </div>
            <div className="card-info">
              <span className="card-step">{card.step}</span>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </div>
            <button
              className="card-button"
              onClick={(event) => {
                event.stopPropagation();
                card.onClick();
              }}
            >
              {card.action} <MdArrowForward />
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

export default CampusDetailView;